/**
 * Cache wrapper for the Trucks list. TtlCache (15-min, single "all" entry, in-flight
 * coalescing) around `API.Data.Trucks.get.all()`. One-shot per Scheduling page load
 * (MC parity — no polling); the TTL spares `get()` callers a refetch.
 *
 * The Scheduling board does NOT read through this TTL: it loads via `refresh()`, on mount
 * and on its refresh gesture, because truck positions move continuously and are written
 * outside the portal. That mount read deliberately trades one live Airtable round-trip for
 * pins that aren't up to 15 min stale (see `refresh()`); the TTL still serves every other
 * `get()` consumer and any remount that arrives between a gesture and the next mount.
 */

import { Truck } from "@domain/CRM/truck";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";

const LIST_TTL_MS = 15 * 60 * 1000;
const LIST_KEY = "all" as const;

/** The one query behind both the TTL fetcher and `refresh()`; `fresh` also bypasses the
 *  server-side Airtable cache (`?fresh=1`). */
const fetchTrucks = (fresh?: boolean): Promise<Truck[]> => API.Data.Trucks.get.all(fresh);

class CachedTrucksList {
	private cache = new TtlCache<typeof LIST_KEY, Truck[]>(LIST_TTL_MS, () => fetchTrucks());

	/** Synchronous TTL cache hit, or null if missing/expired. */
	public peek(): Truck[] | null {
		return this.cache.peek(LIST_KEY);
	}

	public get(): Promise<Truck[]> {
		return this.cache.get(LIST_KEY);
	}

	/**
	 * Bypass BOTH caches — the server's (`?fresh=1`) and this TTL entry — and overwrite the entry
	 * with the live result. Used by the Scheduling board on mount AND on its refresh-in-place
	 * gesture: positions are written to Airtable outside the portal, so neither cache is ever
	 * invalidated by our own writes, and a mount that read through the TTL would show pins up to
	 * 15 min behind the board (plus however long the server cache had left).
	 *
	 * Unlike `get()`, concurrent callers each fetch — this is a deliberate bypass, not a join.
	 * A failed fetch leaves the previous entry in place.
	 */
	public async refresh(): Promise<Truck[]> {
		const trucks = await fetchTrucks(true);
		this.cache.set(LIST_KEY, trucks);
		return trucks;
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedTrucksAPI {
	public list = new CachedTrucksList();
}
